// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Quaternion from '../../math/Quaternion';
import Vector3 from '../../math/Vector3';
import MathUtils from '../../math/MathUtils';

/**
 * Describes a relative transform as a Quaternion-Vector-Vector tuple. We use QVV to make it simpler to do LERP blending.
 */
class TransformData {

  /**
   * @param {TransformData} [source] source to copy.
   */
  constructor(source) {
    this._rotation = new Quaternion().copy(source ? source._rotation : Quaternion.IDENTITY);
    this._scale = new Vector3().copy(source ? source._scale : Vector3.ONE);
    this._translation = new Vector3().copy(source ? source._translation : Vector3.ZERO);
  }

  /*
   * Applies the data from this transformdata to supplied transform (i.e. transform matrices, not TransformData)
   * @param {Transform}
   */
  applyTo(transform) {
    transform.setIdentity();
    // TODO: matrix vs quaternion? [SUMERIAN-2315]
    transform.rotation.copyQuaternion(this._rotation);
    transform.scale.set(this._scale);
    transform.translation.set(this._translation);
    transform.update();
  }

  /**
   * Copy the source's values into this transform data object.
   * @param {TransformData} source our source to copy.
   */
  set(source) {
    this._rotation.copy(source._rotation);
    this._scale.copy(source._scale);
    this._translation.copy(source._translation);
  }

  /**
   * Blend this TransformData with the given TransformData using linear interpolation by the given blendWeight.
   *
   * @param {TransformData} blendTo The TransformData to blend to
   * @param {number} blendWeight The blend weight
   * @param {TransformData} store The TransformData store.
   * @param {boolean} [isAdditive] blendTo is treated as additive if this is true. Defaults to false.
   * @returns {TransformData} The blended transform.
   */
  blend(blendTo, blendWeight, store, isAdditive=false) {
    const tData = store || new TransformData();

    if (isAdditive) {
      // replace the blendTo targets with targets that add the blendTo transformData to this transformData
      workVector1.set(this._translation).add(blendTo._translation);
      workVector2.set(this._scale).mul(blendTo._scale);
      workQuaternion.set(this._rotation).mul(blendTo._rotation);
    } else {
      workVector1.set(blendTo._translation);
      workVector2.set(blendTo._scale);
      workQuaternion.set(blendTo._rotation);
    }

    // blend
    blendWeight = MathUtils.clamp(blendWeight, 0, 1);
    tData._translation.set(this._translation).lerp(workVector1, blendWeight);
    tData._scale.set(this._scale).lerp(workVector2, blendWeight);
    Quaternion.slerp(this._rotation, workQuaternion, blendWeight, tData._rotation);
    return tData;
  }

  /**
   * Return true if this TransformData is approximately equal to the other TransformData
   * @param {TransformData} other The TransformData to compare
   * @return {booelan} true when approximately equal, false otherwise
   */
  equals(other) {
    return this._translation.equals(other._translation) &&
      this._scale.equals(other._scale) &&
      this._rotation.equals(other._rotation);
  }
}

const workVector1 = new Vector3;
const workVector2 = new Vector3;
const workQuaternion = new Quaternion;
export default TransformData;
